import { PageNotFound } from '@shared/PageNotFound/PageNotFound'
import React from 'react'
import { Navigate, Route, Routes } from 'react-router-dom'

import { redirectToV3, V2_SUPPORT_DEPRECATED_FLAG } from '@/constants/featureFlags'
import { useFeatureFlagsContext } from '@/context'
import {
  Dashboard,
  Home,
  Homes,
  Job,
  Jobs,
  Member,
  Members,
  NotificationAlerts,
  Planner,
  Properties,
  Property,
  Reservation,
  Reservations,
  ScheduledCommunications,
  Ticket,
  Tickets,
} from '@/views'

import TicketFiltersContextProvider, {
  currentTicketFiltersKey,
  FiltersState,
  getPreviousSavedTicketFilters,
} from './context/filters/ticketFilters/ticketFilters'
import { AccessLevel, useAccessLevel } from './hooks/useAccessLevel'
import { APP_TYPE } from './types/User'
import { APP_STORAGE_KEY } from './util'
import { HomeQualityChecks } from './views/Home/HomeQualityChecks/HomeQualityChecks'

/**
 * Sends the browser to Mission Control V3. V3 is a different origin, so this
 * can't be a react-router `<Navigate>` — it has to leave the SPA via
 * `window.location`. `replace` keeps the deprecated V2 URL out of history, so
 * the back button doesn't land the user right back on a route that redirects.
 * Renders nothing; the navigation runs in an effect rather than during render.
 */
const V3Redirect = () => {
  React.useEffect(() => {
    redirectToV3()
  }, [])

  return null
}

export const SupportRouter = () => {
  const { AGM, HUB_MANAGER } = AccessLevel
  const { accessGranted } = useAccessLevel()
  const { isEnabled } = useFeatureFlagsContext()
  const active_app = localStorage.getItem(APP_STORAGE_KEY) || APP_TYPE.SUPPORT
  const previousTicketFiltersJSON = localStorage.getItem(currentTicketFiltersKey)

  let initialTicketFilters: FiltersState | undefined = undefined
  if (previousTicketFiltersJSON) {
    const previousTicketFilters = JSON.parse(previousTicketFiltersJSON) as FiltersState
    initialTicketFilters = getPreviousSavedTicketFilters(previousTicketFilters)
  }

  if (active_app !== APP_TYPE.SUPPORT) return null

  const supportDeprecated = isEnabled(V2_SUPPORT_DEPRECATED_FLAG)
  // "/tickets" is itself deprecated once the flag is on, so users who don't
  // have Dashboard access can no longer be sent there from "/" — that would
  // bounce straight back here and loop. Land everyone on the Dashboard instead.
  const showDashboard = supportDeprecated || accessGranted([AGM, HUB_MANAGER])
  const v3Redirect = <V3Redirect />

  return (
    <TicketFiltersContextProvider value={initialTicketFilters}>
      <Routes>
        {showDashboard ? (
          <Route element={<Dashboard />} path="/" />
        ) : (
          <Route element={<Navigate replace={true} to="/tickets" />} path="/" />
        )}
        {accessGranted([AGM, HUB_MANAGER]) && <Route element={<Planner />} path="/planner" />}
        <Route element={supportDeprecated ? v3Redirect : <Homes />} path="/homes" />
        <Route element={supportDeprecated ? v3Redirect : <Home />} path="/homes/:homeId" />
        <Route element={<HomeQualityChecks />} path="/homeQualityChecks" />
        <Route element={supportDeprecated ? v3Redirect : <Properties />} path="/properties" />
        <Route element={supportDeprecated ? v3Redirect : <Property />} path="/properties/:propertyId" />
        <Route element={supportDeprecated ? v3Redirect : <Reservations />} path="/reservations" />
        <Route element={supportDeprecated ? v3Redirect : <Reservation />} path="/reservations/:reservationId" />
        <Route element={supportDeprecated ? v3Redirect : <Members />} path="/members" />
        <Route element={supportDeprecated ? v3Redirect : <Member />} path="/members/:memberId" />
        <Route element={supportDeprecated ? v3Redirect : <Jobs />} path="/jobs" />
        <Route element={supportDeprecated ? v3Redirect : <Job />} path="/jobs/:jobId" />
        <Route element={<ScheduledCommunications />} path="/scheduledCommunications" />
        <Route element={supportDeprecated ? v3Redirect : <Tickets />} path="/tickets" />
        <Route element={supportDeprecated ? v3Redirect : <Ticket />} path="/tickets/:ticketId" />
        <Route element={<NotificationAlerts />} path="/notifications" />
        <Route element={<PageNotFound />} path="*" />
      </Routes>
    </TicketFiltersContextProvider>
  )
}
